/**
 * Documentation Sidebar
 *
 * Hierarchical navigation for docs with:
 * - Collapsible sections
 * - Current page highlighting
 * - Keyboard accessible
 */

import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface NavItem {
  title: string;
  href?: string;
  items?: NavItem[];
}

const navigation: NavItem[] = [
  {
    title: 'Getting Started',
    items: [
      { title: 'Introduction', href: '/docs' },
      { title: 'Quick Start', href: '/docs/getting-started/quick-start' },
      { title: 'Core Concepts', href: '/docs/getting-started/concepts' },
    ],
  },
  {
    title: 'Guides for Org Owners',
    items: [
      { title: 'Setup Organization', href: '/docs/guides/org-owners/setup-organization' },
      { title: 'Manage Members', href: '/docs/guides/org-owners/manage-members' },
      { title: 'Billing', href: '/docs/guides/org-owners/billing' },
    ],
  },
  {
    title: 'Guides for Admins',
    items: [
      { title: 'Events', href: '/docs/guides/admins/events' },
      { title: 'Crew Management', href: '/docs/guides/admins/crew' },
      { title: 'Assignments', href: '/docs/guides/admins/assignments' },
      { title: 'Messaging', href: '/docs/guides/admins/messaging' },
      { title: 'Documents', href: '/docs/guides/admins/documents' },
      { title: 'Resources', href: '/docs/guides/admins/resources' },
      { title: 'Pay Periods', href: '/docs/guides/admins/pay-periods' },
      { title: 'Reports', href: '/docs/guides/admins/reports' },
    ],
  },
  {
    title: 'API Reference',
    items: [
      { title: 'Overview', href: '/docs/api' },
      { title: 'Authentication', href: '/docs/api/authentication' },
      { title: 'Events', href: '/docs/api/events' },
      { title: 'Assignments', href: '/docs/api/assignments' },
      { title: 'Users', href: '/docs/api/users' },
      { title: 'Messages', href: '/docs/api/messages' },
    ],
  },
];

export default function DocsSidebar() {
  return (
    <nav aria-label="Documentation" className="space-y-6">
      {navigation.map((section) => (
        <NavSection key={section.title} section={section} />
      ))}
    </nav>
  );
}

function NavSection({ section }: { section: NavItem }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    // Open by default if any child is active
    return section.items?.some((item) => item.href === location.pathname) ?? true;
  });

  if (!section.items) {
    return null;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors"
        aria-expanded={isOpen}
      >
        {section.title}
        <ChevronIcon
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-90' : ''
          }`}
        />
      </button>

      {isOpen && (
        <ul className="mt-2 space-y-1 border-l border-gray-200 ml-1" role="list">
          {section.items.map((item) => (
            <li key={item.href}>
              <NavLink
                to={item.href!}
                end={item.href === '/docs' || item.href === '/docs/api'}
                className={({ isActive }) =>
                  `block pl-4 py-1.5 text-sm border-l -ml-px transition-colors ${
                    isActive
                      ? 'text-primary-600 font-medium border-primary-600'
                      : 'text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300'
                  }`
                }
              >
                {item.title}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
